// eslint-disable-next-line no-restricted-imports
import { Configuration, DefaultApi as UserSettingsApi } from '@eih-netbroker/api-user-settings';
import { AxiosInstance } from 'axios';
import { IConfigAPI } from '../config';
import { ResourceHeadersWithToken } from '../services/useResource';
import {
  ExtractDataOfUserSettingWithDataMap,
  ExtractUserSettingOfUserSettingWithDataMap,
  SettingRaw,
  UserSettingGetAndMigrate,
} from '../state';
import { BaseResource } from './base.resource';

export type Setting<U extends UserSettingGetAndMigrate<any, any>> = {
  setting:
    | {
        setting: ExtractUserSettingOfUserSettingWithDataMap<U>;
        settingId: string | number;
      }
    | ExtractUserSettingOfUserSettingWithDataMap<U>;
  data: ExtractDataOfUserSettingWithDataMap<U>;
  version: number;
};

export type UpdateUserSettingsQueueElement = {
  request: Array<SettingRaw>;
  headers: ResourceHeadersWithToken;
  resolver: (response: void) => void;
  reject: (error: unknown) => void;
};

export class UserSettingsResource extends BaseResource {
  // Name has to be overridden, since the build job renames it to 'r' and
  // every resource class will have the same name
  // @ts-ignore
  static readonly name = 'UserSettingsResource';
  private userSettingsApi: UserSettingsApi;
  private queue: Array<UpdateUserSettingsQueueElement> = [];
  private timer: NodeJS.Timeout | null = null;

  constructor(axiosInstance: AxiosInstance, basePath: string, configApi: IConfigAPI) {
    super(axiosInstance, `${basePath}${configApi.usUrl.userSettings}`, configApi);
    this.userSettingsApi = new UserSettingsApi(
      new Configuration({ basePath: `${basePath}${configApi.usUrl.userSettings}` }),
      `${basePath}${configApi.usUrl.userSettings}`,
      axiosInstance,
    );
  }

  getVersion() {
    return this.userSettingsApi.getVersion();
  }

  async loadUserSettings(headers: ResourceHeadersWithToken) {
    const { data } = await this.userSettingsApi.getUserSettings({ ...headers, userId: headers.userid });
    return data.componentSettings;
  }

  async updateUserSetting(
    settings: Array<Setting<UserSettingGetAndMigrate<any, any>>>,
    requestHeaders: ResourceHeadersWithToken,
    priority?: boolean,
  ): Promise<void> {
    const componentSettings = settings.map(({ setting, data, version }) => {
      const key = typeof setting === 'object' ? `${setting.setting}/${setting.settingId.toString()}` : setting;
      return {
        data: JSON.stringify(data),
        key: key,
        version: version,
      };
    });

    if (priority) {
      await this.userSettingsApi.saveUserSetting({
        ...requestHeaders,
        userId: requestHeaders.userid,
        userSettings: {
          componentSettings: componentSettings,
        },
      });
    } else {
      return new Promise<void>((resolver, reject) => {
        this.queue.push({ request: componentSettings, headers: requestHeaders, resolver, reject });

        if (!this.timer) {
          this.timer = setTimeout(() => this.flush(), this.configApi.batchedRequestsTimeout.userSettings);
        }
      });
    }
  }

  async flush() {
    if (this.queue.length === 0) {
      return;
    }

    const batchedRequests = this.queue.map(({ request, headers, resolver, reject }) => ({
      request,
      headers,
      reject,
      resolver,
    }));

    this.queue = [];
    this.timer = null;

    try {
      await this.userSettingsApi.saveUserSetting({
        ...batchedRequests[0].headers,
        userId: batchedRequests[0].headers.userid,
        userSettings: {
          componentSettings: batchedRequests.reduce(
            (acc, { request }) => [...acc, ...request],
            [] as Array<SettingRaw>,
          ),
        },
      });

      batchedRequests.forEach(({ resolver }) => resolver());
    } catch (error) {
      batchedRequests.forEach(({ reject }) => reject(error));
    }
  }
}
